import {
  BOAT_RACE_IMAGES,
  BOAT_RACE_REFERENCE_LINKS_EN,
  BOAT_RACE_STREAMING_CHANNELS_NEWS,
  BOAT_RACE_STREAMING_CHANNELS_PUBLIC,
  BOAT_RACE_STREAMING_CHANNELS_TOURISM,
} from './boatRacePostUtils';

const CHANNEL_COUNT =
  BOAT_RACE_STREAMING_CHANNELS_PUBLIC.length +
  BOAT_RACE_STREAMING_CHANNELS_NEWS.length +
  BOAT_RACE_STREAMING_CHANNELS_TOURISM.length;

/** Free YouTube live streams for Kerala boat races — English guide */
export const KERALA_BOAT_RACE_STREAMING_ENGLISH_POST = {
  id: 'featured-kerala-boat-race-streaming-english',
  slug: 'kerala-boat-race-live-stream-youtube-channels',
  title: 'Kerala Boat Race Live Stream: Free YouTube Channels for NTBR & Vallamkali',
  excerpt:
    'Watch the 72nd Nehru Trophy and Kerala vallamkali for free on YouTube — DD Malayalam, Asianet, Manorama, 24 News, and every major Malayalam news channel link in one place.',
  publishedAt: '2026-08-22T01:00:00.000Z',
  coverImageUrl: BOAT_RACE_IMAGES.hero,
  authorName: 'Daily Sports Life',
  clubName: 'Kerala Sports Desk',
  blogCategory: 'boat-races',
  locale: 'en',
  boatRaceTopic: 'streaming',
  alternateSlug: 'kerala-boat-race-live-stream-youtube-malayalam',
  featured: true,
  pinned: true,
  component: 'WellnessGuideBlogArticle',
  theme: 'diet',
  relatedPostsTitle: 'More Kerala boat race guides',
  relatedPosts: [
    {
      slug: 'kerala-boat-race-live-stream-youtube-malayalam',
      title: 'കേരള വള്ളംകളി ലൈവ്: സൗജന്യ YouTube ചാനലുകൾ (മലയാളം)',
      blurb: 'Malayalam edition — same channel list with local viewing tips.',
    },
    {
      slug: 'nehru-trophy-2026-chundan-snake-boats-captains',
      title: '72nd Nehru Trophy 2026: 22 Chundan Boats, Clubs & Captains',
      blurb: 'Full snake-boat line-up, captains, and race-day photos.',
    },
    {
      slug: 'kerala-boat-race-nehru-trophy-ntbr-history',
      title: 'Kerala Boat Race Guide: NTBR 2026 & Nehru Trophy History',
      blurb: 'History, visitor tips, and vallamkali background.',
    },
  ],
  referenceLinks: [
    ...BOAT_RACE_REFERENCE_LINKS_EN,
    {
      label: 'NIC webcast portal (gov.in)',
      url: 'https://webcast.gov.in/nehrutrophy/',
    },
    {
      label: 'NTBR official video gallery',
      url: 'https://nehrutrophy.nic.in/pages-en-IN/videos-index.php',
    },
  ],
  sections: [
    {
      type: 'stats',
      ariaLabel: 'Streaming guide at a glance',
      items: [
        { value: String(CHANNEL_COUNT), label: 'Free YouTube channels' },
        { value: '22 Aug 2026', label: '72nd Nehru Trophy (today)' },
        { value: '11 AM–5 PM', label: 'Typical NTBR window (IST)' },
        { value: '₹0', label: 'YouTube access — no subscription' },
      ],
    },
    {
      type: 'panel',
      title: 'How to watch Kerala boat races free on YouTube',
      paragraphs: [
        'You do not need a cable box or paid OTT app to follow **Kerala vallamkali** from abroad. Every August and Onam season, Malayalam news networks and Doordarshan push **Nehru Trophy Boat Race (NTBR)** live streams to their official **YouTube** channels — free with ads, on phone, laptop, or smart TV.',
        'On **Saturday, 22 August 2026**, the **72nd Nehru Trophy** runs at **Punnamada Lake, Alappuzha**. Heats for smaller boats start around **11:00 AM IST**; the formal inauguration is at **2:00 PM**, followed by chundan heats and the snake-boat final in the afternoon. Bookmark the channels below and search each feed for «Nehru Trophy live» or «വള്ളംകളി ലൈവ്» when the race window opens.',
        'Coverage varies by year — not every channel streams the full programme every time. Start with **DD Malayalam** and **Asianet News**, then open the other news desks if you want alternate commentary or backup streams.',
      ],
      chips: ['Free on YouTube', 'NTBR 2026', 'Malayalam live', 'Vallamkali', 'Punnamada'],
    },
    {
      type: 'videoList',
      title: 'Public broadcasters — primary free streams',
      intro:
        'Doordarshan is the traditional NTBR television partner. Both DD Malayalam and DD National YouTube channels have carried live Punnamada coverage in recent editions.',
      videos: BOAT_RACE_STREAMING_CHANNELS_PUBLIC,
    },
    {
      type: 'videoList',
      title: 'Malayalam news channels — live & highlights',
      intro:
        'These are the major Kerala news YouTube desks that regularly stream NTBR or publish same-day highlight reels. Subscribe before race day to get a «live now» notification.',
      videos: BOAT_RACE_STREAMING_CHANNELS_NEWS,
    },
    {
      type: 'videoList',
      title: 'Tourism & Champions Boat League',
      intro:
        'After NTBR, the **Champions Boat League (CBL)** continues the professional snake-boat season across Kerala backwaters. These official channels post league races and tourism films.',
      videos: BOAT_RACE_STREAMING_CHANNELS_TOURISM,
    },
    {
      type: 'panel',
      title: 'NTBR 2026 — when to tune in (IST)',
      orderedList: [
        '**~11:00 AM** — Heats for smaller boat classes (veppu, iruttukuthy, churulan, etc.).',
        '**2:00 PM** — Formal inauguration; Chief Minister V.D. Satheesan opens the 72nd edition.',
        '**After 2:00 PM** — Chundan (snake boat) heats; six heats with the fastest boats advancing.',
        '**~4:00 PM onward** — Finals block including the chundan vallam championship race.',
        '**Post-race** — Highlight clips on Asianet, Manorama, 24 News, and other desks within hours.',
      ],
      callout:
        'Confirm exact heat timings on nehrutrophy.nic.in — schedules can shift with weather or water conditions on Punnamada.',
    },
    {
      type: 'panel',
      title: 'Tips for a smooth free stream',
      list: [
        'Open each YouTube channel link above and tap **Subscribe** — race-day notifications save frantic searching.',
        'Search inside the channel: «Nehru Trophy live», «NTBR 2026», or Malayalam «നെഹ്‌റു ട്രോഫി ലൈവ്».',
        'If one stream lags, switch channels — Asianet, Manorama, and DD Malayalam often run parallel feeds.',
        'Use the NIC **webcast.gov.in/nehrutrophy** portal as a browser backup when listed for the edition.',
        'Highlights remain free after the live window — search «Nehru Trophy final» on any channel the next morning.',
        'Respect copyright: link only to official channel URLs, not pirated rebroadcasts.',
      ],
    },
    {
      type: 'panel',
      title: 'Other Kerala boat races on YouTube',
      paragraphs: [
        'NTBR is the headline, but the same news channels cover the full **vallamkali** calendar. Search these event names on the channels above when dates approach:',
      ],
      list: [
        '**Champakulam Moolam Boat Race** — season opener on the Pampa; temple-linked tradition.',
        '**President’s Trophy Boat Race** — Kollam backwaters; CBL season finale venue.',
        '**Aranmula Uthrattathi Vallamkali** — sacred pageant on the Pampa (August 30, 2026).',
        '**Payippad Jalotsavam**, **Kumarakom**, **Triprayar**, **Kallada** — regional regattas through Onam.',
        '**Champions Boat League** venue races — Kainakary, Thazhathangadi, Pulinkunnu, Kayamkulam, and more.',
      ],
    },
    {
      type: 'panel',
      title: 'Malayalam commentary from anywhere',
      paragraphs: [
        'Malayalees in the US, UK, Gulf, or anywhere else can watch the same free YouTube streams as viewers on the Punnamada bank — no VPN needed for official news channels. Pair this list with our **Malayalam edition** of this article for identical links with Malayalam viewing notes.',
        'For the full 2026 snake-boat line-up and captain table, see our **72nd Nehru Trophy chundan guide**. For history and visitor logistics, start with the **Kerala boat race NTBR history** article.',
      ],
      callout: {
        text: 'Read this guide in Malayalam (മലയാളം) →',
        url: '/blog/kerala-boat-race-live-stream-youtube-malayalam',
      },
    },
  ],
};
